import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Badge,
  Button,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
} from 'reactstrap';
import moment from 'moment';
import toast from 'react-hot-toast';
import { updatePremiumCare } from '../action/action';
import {
  PREMIUM_CARE_STATUS_OPTIONS,
  statusColor,
  statusLabel,
} from '../constants';



const formatDate = (value, withTime = false) =>
  value
    ? moment(new Date(value)).format(withTime ? 'DD MMM YYYY, h:mm A' : 'DD MMM YYYY')
    : '-';

/**
 * pdfUrl is not always a link - some records carry a bare document
 * reference (e.g. "000000WDE8000682967").
 */
const isUrl = (value) =>
  typeof value === 'string' && /^https?:\/\//i.test(value.trim());

const text = (value) =>
  value === null || value === undefined || value === '' ? '-' : String(value);

const PremiumCareDetailsOffcanvas = ({
  isOpen,
  toggle,
  record,
  isEdit,
  onUpdated,
}) => {
  const dispatch = useDispatch();
  // matchedItems ({ itemCode, productName }) is the newer shape; older
  // records only carry matchedItemCodes.
  const matchedItems = Array.isArray(record?.matchedItems)
    && record.matchedItems.length > 0
    ? record.matchedItems.map((item) => ({
        itemCode: item?.itemCode || '',
        productName: item?.productName || '',
      }))
    : (Array.isArray(record?.matchedItemCodes) ? record.matchedItemCodes : []).map(
        (code) => ({ itemCode: code, productName: '' })
      );

  const [status, setStatus] = useState('');
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset the form whenever a different record is opened.
  useEffect(() => {
    setStatus(record?.status || '');
    setRemarks(record?.remarks || '');
  }, [record?._id, record?.status, record?.remarks]);


  const isDirty =
    status !== (record?.status || '') || remarks !== (record?.remarks || '');

  const handleSave = async () => {
    if (!record?._id) return;
    setSaving(true);
    try {
      const result = await updatePremiumCare({
        params: { id: record._id, status, remarks },
        dispatch,
      });

      if (result?.code === 200) {
        toast.success(result?.message || 'Premium care entry updated');
        if (onUpdated) onUpdated();
        toggle();
      } else {
        toast.error(result?.message || 'Update failed');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      style={{ width: '50vw', maxWidth: '900px' }}
      className="border-start-0 shadow"
    >
      <OffcanvasHeader toggle={toggle} className="border-bottom bg-light py-3">
        <div className="d-flex align-items-center">
          <i
            className={`mdi ${
              isEdit ? 'mdi-pencil-outline' : 'mdi-shield-check-outline'
            } text-warning me-2 fs-4`}
          ></i>
          <span className="fw-bold">
            {isEdit ? 'Update Premium Care' : 'Premium Care'}
          </span>
          <span className="mx-2 text-muted fw-normal">|</span>
          <span className="text-secondary fw-bold">
            {record?.receiptNumber || record?.salesOrderNo || '-'}
          </span>
        </div>
      </OffcanvasHeader>

      <OffcanvasBody className="p-4">
        {record ? (
          <div className="p-4 bg-light rounded-3 border bg-opacity-50">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center">
                <i className="mdi mdi-information-outline text-info me-2 fs-5"></i>
                <h6 className="fw-bold mb-0">
                  {isEdit ? 'Update Status & Remarks' : 'Complete Order Details'}
                </h6>
              </div>
              <Badge
                color={statusColor(record?.status)}
                className="text-uppercase px-3 py-2"
              >
                {statusLabel(record?.status)}
              </Badge>
            </div>

            <div className="row g-4">
              {!isEdit && (
                <>
                {/* Customer Info */}
                <div className="col-12">
                  <div className="p-3 bg-white rounded-3 shadow-sm border-start border-4 border-info">
                    <div className="row">
                      <div className="col-md-6 mb-3 mb-md-0">
                        <label className="text-muted small text-uppercase fw-bold d-block mb-1">
                          Customer Name
                        </label>
                        <span className="fw-semibold text-dark fs-14">
                          {text(record?.customerName)}
                        </span>
                      </div>
                      <div className="col-md-6">
                        <label className="text-muted small text-uppercase fw-bold d-block mb-1">
                          Contact Details
                        </label>
                        <div className="d-flex flex-column">
                          <span className="fw-semibold text-dark fs-14">
                            <i className="mdi mdi-phone-outline me-1"></i>
                            {text(record?.customerMobile)}
                          </span>
                          {record?.customerEmail && (
                            <span className="text-muted small">
                              <i className="mdi mdi-email-outline me-1"></i>
                              {record.customerEmail}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Info */}
                <div className="col-md-6">
                  <label className="text-muted small text-uppercase fw-bold d-block mb-1">
                    Order Info
                  </label>
                  <div className="d-flex flex-column gap-2">
                    <div>
                      <small className="text-muted d-block">Receipt No</small>
                      <span className="fw-bold text-dark">
                        {text(record?.receiptNumber)}
                      </span>
                    </div>
                    <div>
                      <small className="text-muted d-block">Sales Order No</small>
                      <span className="fw-medium">{text(record?.salesOrderNo)}</span>
                      {record?.currency && (
                        <>
                          <span className="mx-1 text-muted">|</span>
                          <span className="fw-medium text-primary">
                            {record.currency}
                          </span>
                        </>
                      )}
                    </div>
                    <div>
                      <small className="text-muted d-block">Customer Id</small>
                      <span className="fw-medium text-break">
                        {text(record?.customerId)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Location Info */}
                <div className="col-md-6">
                  <label className="text-muted small text-uppercase fw-bold d-block mb-1">
                    Location
                  </label>
                  <div className="d-flex flex-column gap-1">
                    <span className="fw-medium text-dark">
                      {text(record?.location)}
                    </span>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {record?.storeCode && (
                        <Badge color="soft-dark" className="fw-normal">
                          {record.storeCode}
                        </Badge>
                      )}
                      {record?.countryCode && (
                        <Badge color="soft-primary" className="fw-normal text-uppercase">
                          {record.countryCode}
                        </Badge>
                      )}
                    </div>
                    {record?.salespersonId && (
                      <div className="mt-2">
                        <small className="text-muted d-block">Salesperson Id</small>
                        <span className="fw-medium">{record.salespersonId}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Matched items */}
                <div className="col-12">
                  <label className="text-muted small text-uppercase fw-bold d-block mb-1">
                    Matched Premium Care Items
                  </label>
                  {matchedItems.length > 0 ? (
                    <div className="d-flex flex-column gap-2">
                      {matchedItems.map((item, index) => (
                        <div
                          key={`${item.itemCode}-${index}`}
                          className="d-flex align-items-center gap-2"
                        >
                          <Badge color="dark" className="fw-normal">
                            {item.itemCode || '-'}
                          </Badge>
                          {item.productName && (
                            <span className="fw-medium text-dark">
                              {item.productName}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted small">No matched items</span>
                  )}
                </div>

                {/* Dates */}
                <div className="col-12">
                  <hr className="my-2 border-dashed" />
                  <div className="row mt-3">
                    <div className="col-md-4 mb-3 mb-md-0">
                      <label className="text-muted small text-uppercase fw-bold d-block mb-1">
                        Order Created
                      </label>
                      <div className="d-flex align-items-center text-success">
                        <i className="mdi mdi-calendar-check me-1 fs-5"></i>
                        <span className="fw-medium">
                          {formatDate(record?.createdAt, true)}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-4 mb-3 mb-md-0">
                      <label className="text-muted small text-uppercase fw-bold d-block mb-1">
                        Status Updated
                      </label>
                      <div className="d-flex align-items-center text-primary">
                        <i className="mdi mdi-progress-clock me-1 fs-5"></i>
                        <span className="fw-medium">
                          {formatDate(record?.statusUpdatedAt, true)}
                        </span>
                      </div>
                      {record?.statusUpdatedBy && (
                        <small className="text-muted">
                          by {record.statusUpdatedBy}
                        </small>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="text-muted small text-uppercase fw-bold d-block mb-1">
                        Last Modified
                      </label>
                      <div className="text-muted small">
                        {formatDate(record?.updatedAt, true)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Invoice */}
                {record?.pdfUrl && (
                  <div className="col-12">
                    <label className="text-muted small text-uppercase fw-bold d-block mb-1">
                      Invoice
                    </label>
                    {isUrl(record.pdfUrl) ? (
                      <Button
                        color="dark"
                        size="sm"
                        outline
                        href={record.pdfUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <i className="mdi mdi-file-pdf-box me-1"></i>
                        View / Download Invoice
                      </Button>
                    ) : (
                      <span className="fw-medium text-break">{record.pdfUrl}</span>
                    )}
                  </div>
                )}

                </>
              )}

              {/* Update status / remarks */}
              {isEdit && (
              <div className="col-12">
                <div className="p-3 bg-white rounded-3 shadow-sm border">
                  <div className="d-flex align-items-center mb-3">
                    <i className="mdi mdi-pencil-outline text-dark me-2 fs-5"></i>
                    <h6 className="fw-bold mb-0">Update</h6>
                  </div>

                  <div className="row">
                    <div className="col-md-4 mb-3 mb-md-0">
                      <Label className="form-label fw-semibold">Status</Label>
                      <Input
                        type="select"
                        value={status}
                        disabled={saving}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <option value="">Select Status</option>
                        {PREMIUM_CARE_STATUS_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </Input>
                    </div>
                    <div className="col-md-8">
                      <Label className="form-label fw-semibold">Remarks</Label>
                      <Input
                        type="textarea"
                        rows={2}
                        placeholder="Add remarks"
                        value={remarks}
                        disabled={saving}
                        onChange={(e) => setRemarks(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <Button color="secondary" size="sm" outline onClick={toggle} disabled={saving}>
                      Cancel
                    </Button>
                    <Button
                      color="dark"
                      size="sm"
                      onClick={handleSave}
                      disabled={saving || !isDirty}
                    >
                      {saving && <Spinner size="sm" className="me-2" />}
                      Save Changes
                    </Button>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-5">
            <i className="mdi mdi-database-off-outline d-block fs-1 mb-2 opacity-25"></i>
            <p className="text-muted">No record selected.</p>
          </div>
        )}
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default PremiumCareDetailsOffcanvas;
